import { useEffect, useRef, useState } from 'react';
import { isHeicFile } from '../utils/function';
import { createMediaPreviewUrl, getFileIdentityKey } from '../utils/mediaPreview';
import { resolveIsHeicFile } from '../utils/heicDetection';

/**
 * Resolves a displayable preview URL for a local upload file.
 * HEIC files are converted to JPEG; other types use a blob URL.
 * Blob URLs are managed by mediaPreview — do not revoke from this hook.
 *
 * @param {File} file
 * @param {string|null} [existingPreviewUrl] - Pre-resolved URL from parent state (non-HEIC)
 * @returns {{ displayUrl: string|null, loading: boolean }}
 */
export const useMediaPreview = (file, existingPreviewUrl = null) => {
  const fileKey = getFileIdentityKey(file);
  const [displayUrl, setDisplayUrl] = useState(() =>
    isHeicFile(file) ? null : (existingPreviewUrl || null)
  );
  const [loading, setLoading] = useState(() => isHeicFile(file));
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const isActive = () => requestId === requestIdRef.current;

    const loadPreview = async () => {
      const needsAsyncPreview =
        isHeicFile(file) || !existingPreviewUrl || (await resolveIsHeicFile(file, isHeicFile));

      if (needsAsyncPreview) {
        setLoading(true);
      }

      try {
        let url = existingPreviewUrl || null;

        if (needsAsyncPreview || !url) {
          url = await createMediaPreviewUrl(file);
        }

        if (isActive()) {
          setDisplayUrl(url);
        }
      } finally {
        if (isActive()) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      requestIdRef.current += 1;
    };
  }, [fileKey, file, existingPreviewUrl]);

  return { displayUrl, loading };
};
